/**
 * Settings modal for GitHub configuration, behaviour preferences, and account management.
 */
import { useState, useEffect } from 'react';
import { useStore } from 'store/useStore';
import QualitySlider, { Quality } from '../ui/QualitySlider';
import { Modal, Button, Box, FormSection, FormLabel, FormHint, FormError } from '../ui';
import { GitHubRepo } from 'api/github';
import { deleteAccount, exportData, disconnectGitHub } from 'api/auth';
import { scanRepoArchitecture, getRepoArchitectureDoc, RepoArchitectureDoc } from 'api/ai';
import { useFeatureAccess, handleAiLimitReached } from 'pricing';
import {
    loadGithubBase,
    loadRepoProjects,
    loadRepoScanStatus,
    setRepoScanStatusCache,
    resetGithubSessionCache,
    GitHubProjectOption,
} from 'utils/githubSettingsCache';

type SettingsProps = {
    onClose: () => void;
    /** Called after the user's account has been successfully deleted. */
    onAccountDeleted: () => void;
};

export default function Settings({ onClose, onAccountDeleted }: SettingsProps) {
    const {
        githubRepo,
        githubProjectId,
        solutionChildType,
        aiSuggestionCount,
        aiModelQuality,
        setGithubRepo,
        setGithubProjectId,
        setSolutionChildType,
        setAiSuggestionCount,
        setAiModelQuality,
        clearAuth,
        gridSpacing,
        snapToGrid,
        setGridSpacing,
        setSnapToGrid,
    } = useStore();

    const [gr, setGr] = useState<string>(githubRepo || '');
    const [childType, setChildType] = useState<'story' | 'experiment'>(solutionChildType || 'story');
    const [suggestionCount, setSuggestionCount] = useState(Math.min(aiSuggestionCount || 3, 3));
    const [modelQuality, setModelQuality] = useState<Quality>((aiModelQuality as Quality) || 'low');
    const [gridSpacingLocal, setGridSpacingLocal] = useState<number>(gridSpacing || 15);
    const [snapLocal, setSnapLocal] = useState<boolean>(snapToGrid || false);
    const [projects, setProjects] = useState<GitHubProjectOption[]>([]);
    const [repos, setRepos] = useState<GitHubRepo[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [projectError, setProjectError] = useState('');
    const [selectedProjectId, setSelectedProjectId] = useState<string>(githubProjectId || '');
    // Gates the entire GitHub box: stays false until connection status, repo list, and
    // (if a repo is already selected) its Projects v2 + scan status have all resolved, so
    // the section appears once, fully populated, instead of piece by piece as each
    // request finishes.
    const [githubSectionReady, setGithubSectionReady] = useState(false);

    // Account & Data section state
    const [exportingData, setExportingData] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [accountError, setAccountError] = useState('');
    const [githubConnected, setGithubConnected] = useState(false);

    // "Scan Codebase" state — pro/teams only, once per 7 days per repo owner (see
    // server.js POST /api/ai/repo-scan). tryScanAccess shows an upgrade prompt itself
    // when the user is on the free tier, so handleScan doesn't need to render its own.
    const { tryAccess: tryScanAccess } = useFeatureAccess('ai_estimation');
    const [scanning, setScanning] = useState(false);
    const [scanMessage, setScanMessage] = useState('');
    const [scanError, setScanError] = useState('');
    const [lastScannedAt, setLastScannedAt] = useState<string | null>(null);
    const [nextScanAvailableAt, setNextScanAvailableAt] = useState<string | null>(null);

    // "View Codebase Summary" modal state — reads the most recently stored
    // architecture document (unlike the scan itself, this read is not rate-limited).
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState('');
    const [summaryDoc, setSummaryDoc] = useState<RepoArchitectureDoc | null>(null);

    // Loads Projects v2 + scan status for a repo together, so both pieces of UI update in
    // one go instead of one after the other. Shared by the initial load and by switching
    // repositories later. Passing '' clears everything (e.g. repo deselected).
    const loadRepoData = async (repoFullName: string) => {
        if (!repoFullName) {
            setProjects([]);
            setProjectError('');
            setLastScannedAt(null);
            setNextScanAvailableAt(null);
            setScanMessage('');
            setScanError('');
            return;
        }
        setLoadingProjects(true);
        const [projectsResult, scanStatusResult] = await Promise.all([
            loadRepoProjects(repoFullName),
            loadRepoScanStatus(repoFullName),
        ]);
        setProjects(projectsResult.projects);
        setProjectError(projectsResult.error);
        setLastScannedAt(scanStatusResult.lastScannedAt);
        setNextScanAvailableAt(scanStatusResult.nextScanAvailableAt);
        setScanMessage('');
        setScanError('');
        setLoadingProjects(false);
    };

    const handleDisconnect = async () => {
        setAccountError('');
        try {
            const result = await disconnectGitHub();
            if (result && result.success) {
                setGithubConnected(false);
                // Clear GitHub selections both in the global store and locally
                setGithubRepo('');
                setGithubProjectId('');
                setGr('');
                setSelectedProjectId('');
                setRepos([]);
                setProjects([]);
                setProjectError('');
                resetGithubSessionCache();
            } else {
                setAccountError(result.error || 'Could not disconnect GitHub');
            }
        } catch (e) {
            setAccountError(e instanceof Error ? e.message : 'Could not disconnect GitHub');
        }
    };

    const formatScanTimestamp = (iso: string) => new Date(iso).toLocaleString();

    /** Architecture-doc list entries may be plain strings or {name, description} objects. */
    const formatSummaryItem = (item: unknown): string => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
            const obj = item as { name?: string; description?: string };
            if (obj.name || obj.description) {
                return obj.description ? `${obj.name || ''}: ${obj.description}`.trim() : (obj.name || '');
            }
        }
        return JSON.stringify(item);
    };

    const handleScan = async () => {
        if (!gr) return;
        if (!tryScanAccess()) return;
        setScanning(true);
        setScanMessage('');
        setScanError('');
        try {
            const result = await scanRepoArchitecture(gr);
            useStore.getState().refreshAiUsage();
            if (result.error) {
                if (!handleAiLimitReached(result)) {
                    setScanError(result.nextAvailableAt
                        ? `You can scan again on ${formatScanTimestamp(result.nextAvailableAt)}.`
                        : result.error);
                }
                if (result.nextAvailableAt) setNextScanAvailableAt(result.nextAvailableAt);
            } else {
                setScanMessage('Codebase scanned successfully. AI estimates will now use this architecture summary.');
                const freshStatus = { lastScannedAt: result.scannedAt || new Date().toISOString(), nextScanAvailableAt: null };
                setLastScannedAt(freshStatus.lastScannedAt);
                setNextScanAvailableAt(freshStatus.nextScanAvailableAt);
                // Keep the session cache in sync so reopening Settings later reflects this
                // scan without re-fetching.
                setRepoScanStatusCache(gr, freshStatus);
            }
        } catch (e) {
            setScanError(e instanceof Error ? e.message : 'Could not scan repository.');
        }
        setScanning(false);
    };

    const handleViewSummary = async () => {
        if (!gr) return;
        setSummaryOpen(true);
        setSummaryLoading(true);
        setSummaryError('');
        setSummaryDoc(null);
        try {
            const result = await getRepoArchitectureDoc(gr);
            if (result.error) {
                setSummaryError(result.error);
            } else {
                setSummaryDoc(result.architectureDoc || null);
            }
        } catch (e) {
            setSummaryError(e instanceof Error ? e.message : 'Could not load codebase summary.');
        }
        setSummaryLoading(false);
    };

    // Keep local component state in sync with store values
    useEffect(() => {
        setGr(githubRepo || '');
    }, [githubRepo]);

    useEffect(() => {
        setGridSpacingLocal(Number(gridSpacing || 15));
    }, [gridSpacing]);

    useEffect(() => {
        setSnapLocal(Boolean(snapToGrid));
    }, [snapToGrid]);

    useEffect(() => {
        setSelectedProjectId(githubProjectId || '');
    }, [githubProjectId]);

    // Loads everything the GitHub box needs — connection status, repo list, and (if a repo
    // is already selected) its Projects v2 + scan status — as one unit, and only flips the
    // section to "ready" once all of it has resolved. loadGithubBase/loadRepoProjects/
    // loadRepoScanStatus each cache their result for the session, so reopening Settings
    // later resolves immediately instead of re-fetching.
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const base = await loadGithubBase();
                if (!mounted) return;
                setGithubConnected(base.githubConnected);
                setRepos(base.repos);
                if (!base.githubConnected) {
                    // Ensure stale repository/project selections are cleared when not connected.
                    setGr('');
                    setSelectedProjectId('');
                    setProjects([]);
                    setProjectError('');
                } else if (githubRepo) {
                    await loadRepoData(githubRepo);
                }
            } finally {
                if (mounted) setGithubSectionReady(true);
            }
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExportData = async () => {
        setExportingData(true);
        setAccountError('');
        try {
            await exportData();
        } catch (e) {
            setAccountError(e instanceof Error ? e.message : 'Export failed. Please try again.');
        }
        setExportingData(false);
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') {
            setAccountError('Type DELETE to confirm account deletion.');
            return;
        }
        setDeletingAccount(true);
        setAccountError('');
        try {
            const result = await deleteAccount();
            if (result.error) {
                setAccountError(result.error);
                setDeletingAccount(false);
                return;
            }
            clearAuth();
            onAccountDeleted();
        } catch (e) {
            setAccountError(e instanceof Error ? e.message : 'Account deletion failed. Please try again.');
            setDeletingAccount(false);
        }
    };

    return (
        <>
        <Modal title='Settings' onClose={onClose} headerVariant='default' closeOnOverlayClick={false}>
            {/*
                Every element below is always mounted, in the same order, at the same size —
                only its content/disabled state changes once githubSectionReady flips to true.
                That keeps the box's height and layout fixed for the whole open→loaded
                transition instead of growing/shrinking as each field's data arrives.
            */}
            <Box className='box--github'>
                <FormSection>
                    <div className='form-project-load'>
                        {githubSectionReady && githubConnected ? (
                            <Button variant='danger' block onClick={handleDisconnect}>
                                Disconnect from GitHub
                            </Button>
                        ) : (
                            <Button
                                variant='github'
                                block
                                disabled={!githubSectionReady}
                                onClick={() => {
                                    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
                                    const returnTo = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
                                    window.location.href = `${apiBase}/api/auth/github?returnTo=${returnTo}`;
                                }}
                            >
                                <img src='/github-logo-1.png' alt='GitHub' />
                                {githubSectionReady ? 'Connect to GitHub' : 'Loading GitHub status…'}
                            </Button>
                        )}
                    </div>

                    <div className='form-row'>
                        <FormLabel>Repository</FormLabel>
                        <select
                            className='form-input'
                            value={githubSectionReady ? gr : ''}
                            onChange={(e) => {
                                const newRepo = e.target.value;
                                setGr(newRepo);
                                setGithubRepo(newRepo);
                                loadRepoData(newRepo);
                            }}
                            disabled={!githubSectionReady || !githubConnected}
                        >
                            {!githubSectionReady ? (
                                <option value=''>Loading repositories…</option>
                            ) : repos.length > 0 ? (
                                <>
                                    <option value=''>— Select a repository —</option>
                                    {repos.map((r) => (
                                        <option key={r.id} value={r.full_name}>
                                            {r.full_name}
                                        </option>
                                    ))}
                                </>
                            ) : (
                                <option value=''>— No repositories found —</option>
                            )}
                        </select>
                    </div>
                </FormSection>

                <FormSection>
                    <div className='form-section-header'>
                        <FormLabel>GitHub Project</FormLabel>
                        <span className='form-current-project'>
                            {selectedProjectId ? `Project ID ${selectedProjectId} connected` : 'No project selected'}
                        </span>
                    </div>

                    <div className='form-project-row form-project-load'>
                        <select
                            className='form-input flex-fill'
                            value={githubSectionReady ? selectedProjectId : ''}
                            onChange={(e) => {
                                setSelectedProjectId(e.target.value);
                                setGithubProjectId(e.target.value);
                            }}
                            disabled={!githubSectionReady || loadingProjects || projects.length === 0}
                        >
                            {!githubSectionReady || loadingProjects ? (
                                <option value=''>Loading projects…</option>
                            ) : (
                                <>
                                    <option value=''>— None —</option>
                                    {projects
                                        .filter((p) => p != null)
                                        .map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.title || p.id}
                                            </option>
                                        ))}
                                </>
                            )}
                        </select>
                    </div>

                    {/* Reserved slot: always present so an error appearing/disappearing
                        doesn't shift anything below it. */}
                    <div className='form-error-slot'>
                        {githubSectionReady && projectError && <FormError>{projectError}</FormError>}
                    </div>
                </FormSection>

                <FormSection>
                    <FormLabel>Codebase Analysis</FormLabel>
                    <FormHint>
                        Scan your connected repository so AI story point estimates can be grounded in its
                        architecture. Available once every 7 days.
                    </FormHint>
                    <div className='settings-codebase-actions'>
                        <Button
                            variant='secondary'
                            block
                            className='btn-codebase-action'
                            disabled={!githubSectionReady || !githubConnected || !gr || scanning || !!nextScanAvailableAt}
                            onClick={handleScan}
                        >
                            {scanning ? 'Scanning…' : 'Scan Codebase'}
                        </Button>
                        <Button
                            variant='secondary'
                            block
                            className='btn-codebase-action'
                            disabled={!githubSectionReady || !githubConnected || !gr || !lastScannedAt}
                            onClick={handleViewSummary}
                        >
                            View Codebase Summary
                        </Button>
                    </div>
                    {/* Reserved slot: always present (fixed min-height in CSS) so scan
                        results/errors popping in or switching repos doesn't move the
                        rest of the modal. */}
                    <div className='settings-codebase-status'>
                        {githubSectionReady && lastScannedAt && (
                            <FormHint>Last scanned: {formatScanTimestamp(lastScannedAt)}</FormHint>
                        )}
                        {githubSectionReady && nextScanAvailableAt && !scanError && (
                            <FormHint>You can scan again on {formatScanTimestamp(nextScanAvailableAt)}.</FormHint>
                        )}
                        {githubSectionReady && scanMessage && <FormHint>{scanMessage}</FormHint>}
                        {githubSectionReady && scanError && <FormError>{scanError}</FormError>}
                    </div>
                </FormSection>
            </Box>

            <Box className='box--ai'>
                <FormSection>
                    <FormLabel>AI suggestions per node slicing request</FormLabel>
                    <select
                        className='form-input'
                        value={suggestionCount}
                        onChange={(e) => {
                            const v = Math.min(parseInt(e.target.value, 10), 3);
                            setSuggestionCount(v);
                            setAiSuggestionCount(v);
                        }}
                    >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                    </select>
                    <FormHint>How many distinct slicing suggestions the AI returns (1–3). Default is 3.</FormHint>
                </FormSection>

                <FormSection>
                    <FormLabel>AI model quality</FormLabel>
                    <QualitySlider
                        value={modelQuality}
                        onChange={(q) => {
                            setModelQuality(q);
                            setAiModelQuality(q);
                        }}
                    />
                    <FormHint>
                        Applies to node slicing, insights summaries, opportunity suggestions, and story
                        estimation. "Scan Codebase" below always uses the highest quality, regardless of
                        this setting, since it's a rare, cached analysis that every later estimate builds on.
                    </FormHint>
                </FormSection>
            </Box>
            <Box className='box--grid'>
                <div className="settings-row">
                    <label className="settings-checkbox-label">
                        <input
                            type='checkbox'
                            checked={snapLocal}
                            onChange={(e) => {
                                setSnapLocal(e.target.checked);
                                setSnapToGrid(e.target.checked);
                            }}
                        />
                        <span>Snap nodes to grid</span>
                    </label>

                    <div className="settings-row--right">
                        <label>Spacing</label>
                        <input
                            className='form-input form-input--spacing'
                            type='number'
                            min={8}
                            value={gridSpacingLocal}
                            onChange={(e) => setGridSpacingLocal(Number(e.target.value))}
                            onBlur={() => setGridSpacing(Math.max(8, Math.round(gridSpacingLocal || 15)))}
                        />
                    </div>
                </div>
                {/* <FormHint>Distance between grid points (canvas units).</FormHint> */}
            </Box>

            <FormSection>
                <FormLabel>Solution child type</FormLabel>
                <select
                    className='form-input'
                    value={childType}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const v = e.target.value as 'story' | 'experiment';
                        setChildType(v);
                        setSolutionChildType(v);
                    }}
                >
                    <option value='story'>Story</option>
                    <option value='experiment'>Experiment</option>
                </select>
                <FormHint>Use Stories or Torres-style Experiments under each Solution.</FormHint>
            </FormSection>

            <FormSection>
                <FormLabel>Account &amp; Data</FormLabel>
                <div className='box box--danger'>
                    <div className='form-account-row'>
                        <Button variant='secondary' onClick={handleExportData} disabled={exportingData}>
                            {exportingData ? 'Exporting…' : 'Export my data'}
                        </Button>
                        <FormHint>Download a JSON copy of all your personal data (GDPR Art.&nbsp;20).</FormHint>
                    </div>

                    <div className='form-account-row form-row'>
                        <div className='form-delete-row'>
                            <input
                                className='form-input'
                                type='text'
                                placeholder='Type DELETE to confirm'
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                            />
                            <Button
                                variant='danger'
                                onClick={handleDeleteAccount}
                                disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
                            >
                                {deletingAccount ? 'Deleting…' : 'Delete my account'}
                            </Button>
                        </div>
                        <FormHint className='form-hint--danger'>
                            Permanently deletes your account and all associated projects. This action cannot be undone.
                            Type <strong>DELETE</strong> to confirm.
                        </FormHint>
                    </div>

                    {accountError && <FormError>{accountError}</FormError>}
                </div>
            </FormSection>
        </Modal>

        {summaryOpen && (
            <Modal
                title='Codebase Summary'
                onClose={() => setSummaryOpen(false)}
                closeOnOverlayClick
            >
                {summaryLoading && <FormHint>Loading…</FormHint>}
                {!summaryLoading && summaryError && <FormError>{summaryError}</FormError>}
                {!summaryLoading && !summaryError && summaryDoc && (
                    <div className='codebase-summary'>
                        {summaryDoc.summary && <p className='codebase-summary-text'>{summaryDoc.summary}</p>}

                        {Array.isArray(summaryDoc.tech_stack) && summaryDoc.tech_stack.length > 0 && (
                            <div className='codebase-summary-section'>
                                <FormLabel>Tech Stack</FormLabel>
                                <ul>
                                    {summaryDoc.tech_stack.map((item, i) => (
                                        <li key={i}>{formatSummaryItem(item)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {Array.isArray(summaryDoc.modules) && summaryDoc.modules.length > 0 && (
                            <div className='codebase-summary-section'>
                                <FormLabel>Modules</FormLabel>
                                <ul>
                                    {summaryDoc.modules.map((item, i) => (
                                        <li key={i}>{formatSummaryItem(item)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {Array.isArray(summaryDoc.complexity_signals) && summaryDoc.complexity_signals.length > 0 && (
                            <div className='codebase-summary-section'>
                                <FormLabel>Complexity Signals</FormLabel>
                                <ul>
                                    {summaryDoc.complexity_signals.map((item, i) => (
                                        <li key={i}>{formatSummaryItem(item)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {Array.isArray(summaryDoc.notable_areas) && summaryDoc.notable_areas.length > 0 && (
                            <div className='codebase-summary-section'>
                                <FormLabel>Notable Areas</FormLabel>
                                <ul>
                                    {summaryDoc.notable_areas.map((item, i) => (
                                        <li key={i}>{formatSummaryItem(item)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
                {!summaryLoading && !summaryError && !summaryDoc && (
                    <FormHint>No summary available yet — scan your codebase first.</FormHint>
                )}
            </Modal>
        )}
        </>
    );
}
